import { Col, Modal, Row, Typography, Table, Button } from 'antd';

import React, { ReactElement, useEffect, useState } from 'react';

import { ColumnsType } from 'antd/lib/table';

import { FormattedMessage, useIntl } from 'react-intl';

import { Key } from 'antd/es/table/interface';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { ModalProps } from '@/types/ModalProps';
import { CheckOutlined } from '@ant-design/icons';

import {
  ListSuppliersQuery,
  Supplier,
  SupplierReduced,
} from '@/core/domain/people/Suppliers/Suppliers.types';
import { useSuppliersReduce } from '@/core/domain/people/Suppliers/Suppliers.hooks';

import LoadMoreSummary from '../LoadMoreSummary';
import SuppliersSelectedFilters from './SuppliersSelectedFilters';
import { applyMask } from '@/utils/mask';
import { cnpjMask, cpfMask } from '@/utils/mask/commonMasks';
import { useDebounce } from '@/hooks';

interface SelectedSuppliersProps extends ModalProps {
  finishCallback?: (supplierIds?: number[]) => void;
  selectedSuppliers?: Supplier[];
  selectSuppliersIds?: number[];
  noSelection?: boolean;
  onSupplierSelect?: (supplier: SupplierReduced) => void;
}

const initialQueryParams: ListSuppliersQuery = {
  order: 'ASC',
  active: true,
  showGlobal: false,
};

const SuppliersSelectedModal: React.FC<SelectedSuppliersProps> = ({
  visible,
  finishCallback,
  handleVisible,
  selectedSuppliers = [],
  selectSuppliersIds = [],
  noSelection,
  onSupplierSelect,
}) => {
  const intl = useIntl();

  const { Title } = Typography;

  const [queryParams, setQueryParams] =
    useState<ListSuppliersQuery>(initialQueryParams);

  const queryDebounce = useDebounce(queryParams, 500);

  const [selectedSuppliersKeys, setSelectedSuppliersKeys] = useState<Key[]>([]);

  const { data, isFetching, hasNextPage, fetchNextPage } = useSuppliersReduce(
    queryDebounce,
    visible,
  );

  const suppliers = flatInfiniteData(data);

  useEffect(() => {
    if (visible) {
      if (selectSuppliersIds.length) {
        setSelectedSuppliersKeys(selectSuppliersIds);
      } else {
        setSelectedSuppliersKeys(
          selectedSuppliers.map((supplier) => supplier.id),
        );
      }
    }
  }, [visible]);

  const handleOk = (): void => {
    if (finishCallback) {
      finishCallback(selectedSuppliersKeys as number[]);
    }

    handleCancel();
  };

  const handleCancel = (): void => {
    setQueryParams(initialQueryParams);

    setSelectedSuppliersKeys([]);

    handleVisible();
  };

  const columns: ColumnsType<SupplierReduced> = [
    {
      dataIndex: 'name',
      title: intl.formatMessage({ id: 'suppliers.list.header.name' }),
      ellipsis: true,
      width: '40%',
    },
    {
      dataIndex: 'fantasyName',
      title: <FormattedMessage id="customers.list.header.fantasyName" />,
      ellipsis: true,
      width: '40%',
    },
    {
      dataIndex: 'cpfCnpj',
      title: <FormattedMessage id="customers.list.header.cpfCnpj" />,
      render: (_, record) =>
        (record.cnpj && applyMask(record.cnpj, cnpjMask)) ||
        (record.cpf && applyMask(record.cpf, cpfMask)),
      ellipsis: true,
      width: '40%',
    },
    onSupplierSelect
      ? {
          dataIndex: 'actions',
          align: 'right',
          render: (_, row) => (
            <Button
              type="primary"
              onClick={() => onSupplierSelect(row)}
              icon={<CheckOutlined />}
            ></Button>
          ),
          width: '20%',
        }
      : {},
  ];

  const handleSuppliersSelect = (selectedRowKeys: Key[]) => {
    setSelectedSuppliersKeys(selectedRowKeys);
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: 'suppliers.modal.selectedSuppliers.title.add',
      })}
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={800}
    >
      <Row gutter={16}>
        <Col flex="1">
          <div style={{ width: '100%' }}>
            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>
                {intl.formatMessage({
                  id: 'suppliers.modal.selectedSuppliers.search',
                })}
              </Title>
            </div>

            <SuppliersSelectedFilters setQueryParams={setQueryParams} />

            <Table<SupplierReduced>
              columns={columns}
              dataSource={suppliers}
              loading={isFetching}
              className="scroll-table"
              rowKey={(row) => row.id}
              pagination={false}
              summary={() =>
                hasNextPage ? (
                  <LoadMoreSummary
                    onClick={fetchNextPage}
                    columnsCount={columns.length + 1}
                  />
                ) : null
              }
              rowSelection={
                !noSelection
                  ? {
                      selectedRowKeys: selectedSuppliersKeys,
                      onChange: handleSuppliersSelect,
                      preserveSelectedRowKeys: true,
                      renderCell: (_, record, __, originNode) => {
                        const cloned = React.cloneElement(
                          originNode as ReactElement,
                        );

                        return cloned;
                      },
                    }
                  : undefined
              }
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default SuppliersSelectedModal;
